import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["participantsModal", "tabModal", "tabModalTitle", "tabModalContent"]

  connect() {
    this.setupModalListeners()
    this.lockMainOverflow()
    this.setupSidebarScrollFix()
  }

  disconnect() {
    this.unlockMainOverflow()
    this.teardownSidebarScrollFix()
  }

  lockMainOverflow() {
    this.mainElement = this.element.closest("main")
    if (this.mainElement) {
      this.originalOverflow = this.mainElement.style.overflow
      this.mainElement.style.overflow = "hidden"
    }
  }

  unlockMainOverflow() {
    if (this.mainElement) {
      this.mainElement.style.overflow = this.originalOverflow || ""
      this.mainElement = null
    }
  }

  // Fix: wheel events over links/icons inside the sidebar don't scroll the container
  // in some browsers. We intercept wheel on the sidebar and manually scroll the
  // nearest overflow-y-auto child.
  setupSidebarScrollFix() {
    const sidebar = this.element.querySelector('[class*="shrink-0"][class*="overflow-hidden"]')
    if (!sidebar) return

    this._sidebarWheelHandler = (e) => {
      // Find the VISIBLE tab content first, then its scroll container
      const visibleTab = sidebar.querySelector('[data-tabs-target="content"]:not(.hidden)')
      const scrollable = visibleTab?.querySelector('[class*="overflow-y-auto"]')
      if (!scrollable) return

      const { scrollTop, scrollHeight, clientHeight } = scrollable
      const maxScroll = scrollHeight - clientHeight
      if (maxScroll <= 0) return // nothing to scroll

      const delta = e.deltaY
      const atTop = scrollTop <= 0 && delta < 0
      const atBottom = scrollTop >= maxScroll && delta > 0

      if (!atTop && !atBottom) {
        scrollable.scrollTop += delta
        e.preventDefault()
        e.stopPropagation()
      }
    }

    sidebar.addEventListener('wheel', this._sidebarWheelHandler, { passive: false })
  }

  teardownSidebarScrollFix() {
    const sidebar = this.element.querySelector('[class*="shrink-0"][class*="overflow-hidden"]')
    if (sidebar && this._sidebarWheelHandler) {
      sidebar.removeEventListener('wheel', this._sidebarWheelHandler)
      this._sidebarWheelHandler = null
    }
  }

  setupModalListeners() {
    // Setup tab modal click listeners
    const tabModal = document.querySelector('[data-facilitator-conversation-target="tabModal"]')
    if (tabModal) {
      tabModal.addEventListener('click', (e) => {
        if (e.target === tabModal) {
          this.closeTabModal()
        }
      })

      const closeButtons = tabModal.querySelectorAll('[data-action*="closeTabModal"]')
      closeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.closeTabModal()
        })
      })
    }

    // Setup participants modal click listeners
    const participantsModal = document.querySelector('[data-facilitator-conversation-target="participantsModal"]')
    if (participantsModal) {
      participantsModal.addEventListener('click', (e) => {
        if (e.target === participantsModal) {
          this.closeParticipantsModal()
        }
      })

      const closeButtons = participantsModal.querySelectorAll('[data-action*="closeParticipantsModal"]')
      closeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          this.closeParticipantsModal()
        })
      })
    }
  }

  openParticipantsModal() {
    const modal = this.hasParticipantsModalTarget
      ? this.participantsModalTarget
      : document.querySelector('[data-facilitator-conversation-target="participantsModal"]')

    if (modal) {
      modal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  closeParticipantsModal() {
    const modal = this.hasParticipantsModalTarget
      ? this.participantsModalTarget
      : document.querySelector('[data-facilitator-conversation-target="participantsModal"]')

    if (modal) {
      modal.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  openTabModal(event) {
    const tabValue = event.currentTarget.dataset.value
    const modal = this.hasTabModalTarget
      ? this.tabModalTarget
      : document.querySelector('[data-facilitator-conversation-target="tabModal"]')

    if (modal) {
      const titleElement = this.hasTabModalTitleTarget
        ? this.tabModalTitleTarget
        : modal.querySelector('[data-facilitator-conversation-target="tabModalTitle"]')

      if (titleElement) {
        titleElement.textContent = tabValue === "progress" ? "Progress" : "Attachments"
      }

      const contentElements = this.hasTabModalContentTarget
        ? this.tabModalContentTargets
        : modal.querySelectorAll('[data-facilitator-conversation-target="tabModalContent"]')

      contentElements.forEach(content => {
        const contentTab = content.dataset.tabContent
        if (contentTab === tabValue) {
          content.classList.remove("hidden")
        } else {
          content.classList.add("hidden")
        }
      })

      modal.classList.remove("hidden")
      document.body.style.overflow = "hidden"
    }
  }

  closeTabModal() {
    const modal = this.hasTabModalTarget
      ? this.tabModalTarget
      : document.querySelector('[data-facilitator-conversation-target="tabModal"]')

    if (modal) {
      modal.classList.add("hidden")
      document.body.style.overflow = ""
    }
  }

  stopPropagation(event) {
    // Prevent clicks inside modal from closing it
    event.stopPropagation()
  }
}
